<template>
  <modal-card
    title="Confirm Contact Info"
    fixed
    padding
    :loading="loading"
    @close="$emit('close')"
  >
    <v-form v-model="isFormValid" @submit.prevent>
      <v-row class="mt-0">
        <v-col cols="6">
          <v-text-field
            v-model="form.first_name"
            label="First Name"
            dense
            :rules="requiredRules('First Name')"
            :disabled="saving"
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model="form.last_name"
            label="Last Name"
            dense
            :rules="requiredRules('Last Name')"
            :disabled="saving"
          />
        </v-col>
      </v-row>

      <v-text-field
        v-model="form.email"
        label="Email"
        class="mt-3"
        dense
        :rules="emailRules"
        :disabled="saving"
      />

      <v-text-field
        v-model="form.zip_code"
        label="Zip Code"
        class="mt-3"
        dense
        :rules="requiredRules('Zip Code')"
        :disabled="saving"
      />

      <v-select
        v-model="form.gender"
        label="Gender"
        class="mt-3"
        :items="genderOptions"
        clearable
        :disabled="saving"
      />

      <v-select
        v-model="form.age_bracket"
        label="Age Bracket"
        class="mt-3"
        :items="ageBracketOptions"
        clearable
        :disabled="saving"
      />
    </v-form>

    <template #actions>
      <v-btn
        color="appMain"
        :loading="saving"
        :disabled="!isFormValid"
        @click="confirm"
      >
        Continue
      </v-btn>
    </template>
  </modal-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import validationRules from '@/lib/validationRules';

const FIELDS = [
  'first_name',
  'last_name',
  'email',
  'zip_code',
  'gender',
  'age_bracket',
];

export default {
  name: 'ContactInfoConfirmation',
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    contactId: String,
    loading: Boolean,
  },
  data() {
    return {
      isFormValid: false,
      saving: false,
      form: {
        first_name: '',
        last_name: '',
        email: '',
        zip_code: '',
        gender: '',
        age_bracket: '',
      },
      genderOptions: ['male', 'female', 'unknown'],
      ageBracketOptions: ['18 or older', '17 & HS grad', 'unqualified age'],
    };
  },
  computed: {
    ...mapGetters('fields', ['getSingleField']),
    emailRules() {
      return [
        (v) => validationRules.checkRequired(v, 'Email'),
        (v) => validationRules.checkEmail(v) || 'Email must be valid',
      ];
    },
  },
  watch: {
    contact: {
      immediate: true,
      handler(val) {
        if (!val) return;
        FIELDS.forEach((key) => {
          this.form[key] = val[key] || '';
        });
      },
    },
  },
  methods: {
    ...mapActions(['updateContact']),

    requiredRules(fieldName) {
      return [(v) => validationRules.checkRequired(v, fieldName)];
    },

    async confirm() {
      if (!this.isFormValid) return;

      this.saving = true;

      try {
        await this.saveFields();
        this.$emit('confirm');
      } finally {
        this.saving = false;
      }
    },

    async saveFields() {
      const changedFields = FIELDS.filter(
        (key) => this.form[key] !== (this.contact?.[key] || '')
      );

      if (!changedFields.length) return;

      const params = {};

      for (const field of changedFields) {
        const fieldSchema = this.getSingleField(field);
        if (!fieldSchema) continue;

        if (!params[fieldSchema.bucketId]) {
          params[fieldSchema.bucketId] = {};
        }
        params[fieldSchema.bucketId][fieldSchema.name] = this.form[field];
      }

      await this.updateContact({
        contactId: this.contactId,
        params,
        skipRefetch: true,
      });
    },
  },
};
</script>
